import {
    Button,
    CircularProgress,
    createStyles,
    FormControlLabel,
    Grid,
    makeStyles,
    Switch,
    TextField,
    Typography
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { IHappyHourAdminConfig } from '../../models/IHappyHour';
import HappyHourService from '../../services/happy-hour/happy-hour.service';
import Card from '../Card/Card.component';

const useStyles = makeStyles((theme) =>
    createStyles({
        card: {
            width: '100%',
        },
        title: {
            color: '#fff',
            marginBottom: theme.spacing(1),
        },
        subtitle: {
            color: 'rgba(255,255,255,0.72)',
            marginBottom: theme.spacing(3),
        },
        field: {
            width: '100%',
        },
        actions: {
            marginTop: theme.spacing(2),
        },
        meta: {
            color: 'rgba(255,255,255,0.72)',
            marginTop: theme.spacing(2),
        },
        error: {
            color: '#ff8a80',
            marginTop: theme.spacing(2),
        },
        loadingWrap: {
            display: 'flex',
            justifyContent: 'center',
            padding: theme.spacing(6),
        }
    })
);

const toText = (value: any) => value === undefined || value === null ? "" : String(value);

const formatTimestamp = (value?: string | null) => {
    if (!value) {
        return "Never";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString();
}

export default function HappyHourAdmin() {
    const classes = useStyles();
    const [config, setConfig] = useState<IHappyHourAdminConfig | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [address, setAddress] = useState("");
    const [lat, setLat] = useState("");
    const [lng, setLng] = useState("");
    const [radiusMiles, setRadiusMiles] = useState("5");
    const [cooldownMinutes, setCooldownMinutes] = useState("10");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const syncForm = (nextConfig: IHappyHourAdminConfig) => {
        setConfig(nextConfig);
        setEnabled(!!nextConfig.enabled);
        setAddress(toText(nextConfig.location?.address));
        setLat(toText(nextConfig.location?.lat));
        setLng(toText(nextConfig.location?.lng));
        setRadiusMiles(toText(nextConfig.radiusMiles || 5));
        setCooldownMinutes(toText(nextConfig.cooldownMinutes || 10));
    }

    useEffect(() => {
        let isMounted = true;

        HappyHourService.getAdminConfig()
            .then((response) => {
                if (!isMounted) {
                    return;
                }

                syncForm(response);
            })
            .catch((nextError) => {
                if (!isMounted) {
                    return;
                }

                setError(nextError instanceof Error ? nextError.message : "Unable to load Happy Hour settings.");
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const onSave = async () => {
        setError("");
        setNotice("");

        const radius = Number(radiusMiles);
        if (!Number.isFinite(radius) || radius <= 0) {
            setError("Radius must be a positive number.");
            return;
        }

        const cooldown = Number(cooldownMinutes);
        if (!Number.isFinite(cooldown) || cooldown <= 0) {
            setError("Cooldown must be a positive number.");
            return;
        }

        const parsedLat = lat.trim() === "" ? undefined : Number(lat);
        const parsedLng = lng.trim() === "" ? undefined : Number(lng);
        if ((parsedLat !== undefined && !Number.isFinite(parsedLat)) || (parsedLng !== undefined && !Number.isFinite(parsedLng))) {
            setError("Latitude and longitude must be valid numbers.");
            return;
        }

        if ((parsedLat === undefined) !== (parsedLng === undefined)) {
            setError("Latitude and longitude must be provided together.");
            return;
        }

        const payload: Partial<IHappyHourAdminConfig> & { location?: any } = {
            enabled,
            radiusMiles: radius,
            cooldownMinutes: cooldown,
            location: {
                address: address.trim()
            }
        };

        if (parsedLat !== undefined && parsedLng !== undefined) {
            payload.location.lat = parsedLat;
            payload.location.lng = parsedLng;
        }

        setIsSaving(true);
        try {
            const response = await HappyHourService.updateAdminConfig(payload);
            syncForm(response);
            setNotice("Happy Hour settings saved.");
        } catch (nextError) {
            setError(nextError instanceof Error ? nextError.message : "Unable to save Happy Hour settings.");
        } finally {
            setIsSaving(false);
        }
    }

    if (isLoading) {
        return (
            <Card>
                <div className={classes.loadingWrap}>
                    <CircularProgress color="primary" />
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <Grid container spacing={3} className={classes.card}>
                <Grid item xs={12}>
                    <Typography variant="h5" className={classes.title}>
                        Happy Hour Admin
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Manage the production-style Happy Hour configuration used by the customer app.
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={enabled}
                                onChange={(_, checked) => setEnabled(checked)}
                                color="primary"
                            />
                        )}
                        label={enabled ? "Happy Hour is enabled" : "Happy Hour is disabled"}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        className={classes.field}
                        label="Address"
                        variant="outlined"
                        value={address}
                        onChange={(event) => setAddress(event.target.value)}
                        helperText="You can update by address only, or pair it with exact coordinates."
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        className={classes.field}
                        label="Latitude"
                        variant="outlined"
                        value={lat}
                        onChange={(event) => setLat(event.target.value)}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        className={classes.field}
                        label="Longitude"
                        variant="outlined"
                        value={lng}
                        onChange={(event) => setLng(event.target.value)}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        className={classes.field}
                        label="Radius (miles)"
                        variant="outlined"
                        type="number"
                        value={radiusMiles}
                        onChange={(event) => setRadiusMiles(event.target.value)}
                        inputProps={{ min: 1, step: 0.1 }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        className={classes.field}
                        label="Cooldown (minutes)"
                        variant="outlined"
                        type="number"
                        value={cooldownMinutes}
                        onChange={(event) => setCooldownMinutes(event.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                    />
                </Grid>

                <Grid item xs={12} className={classes.actions}>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={isSaving}
                        onClick={onSave}
                    >
                        {isSaving ? "Saving..." : "Save Happy Hour Settings"}
                    </Button>
                </Grid>

                {(error || notice) && (
                    <Grid item xs={12}>
                        <Typography className={error ? classes.error : classes.meta}>
                            {error || notice}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Typography className={classes.meta}>
                        Last updated: {formatTimestamp(config?.updatedAt)}
                    </Typography>
                    <Typography className={classes.meta}>
                        Updated by: {config?.updatedBy || "Unknown"}
                    </Typography>
                </Grid>
            </Grid>
        </Card>
    )
}
